/**
 * Authentication Service
 *
 * Centralized service for all authentication-related API calls.
 * Handles login, logout, token refresh, and guest authentication.
 *
 * This service uses cookie-based authentication (HTTP-only cookies) for security.
 * Refresh tokens are automatically stored in HTTP-only cookies by the server.
 */

import axios from 'axios';

class AuthService {
  /**
   * Login with username and password
   * @param {string} username
   * @param {string} password
   * @param {string} urlPrefix - Not used for API calls (APIs don't have locale prefix)
   * @returns {Promise<{accessToken: string, user: Object}>}
   */
  async login(username, password, urlPrefix = '/') {
    // API endpoints don't use locale prefix
    // Using Symfony's default json_login endpoint
    const response = await axios.post('/api/login_check', {
      username,
      password,
    }, {
      headers: {
        'X-Auth-Mode': 'cookie', // Always use cookie mode for web browsers (secure)
      },
      withCredentials: true, // Required to receive/send httpOnly cookies
    });

    return response.data;
  }

  /**
   * Logout and invalidate tokens
   * Refresh token is automatically sent via HTTP-only cookie
   * @param {string} urlPrefix - Not used for API calls (APIs don't have locale prefix)
   * @returns {Promise<void>}
   */
  async logout(urlPrefix = '/') {
    try {
      // API endpoints don't use locale prefix
      // Using Symfony's default logout endpoint
      // Refresh token is automatically sent via HTTP-only cookie
      await axios.post('/api/logout', {}, {
        withCredentials: true, // Required to send httpOnly cookies
      });
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with client-side logout even if server call fails
    }
  }

  /**
   * Refresh access token using refresh token from httpOnly cookie
   * Refresh token is automatically sent via HTTP-only cookie
   * @param {string} urlPrefix - Not used for API calls (APIs don't have locale prefix)
   * @returns {Promise<{accessToken: string, user: Object}>}
   */
  async refreshToken(urlPrefix = '/') {
    // API endpoints don't use locale prefix
    // Refresh token is automatically sent via HTTP-only cookie
    const response = await axios.post(
      '/api/auth/refresh',
      {},
      {
        headers: {
          'X-Auth-Mode': 'cookie',
        },
        withCredentials: true, // Required to send/receive httpOnly cookies
      }
    );

    return response.data;
  }

  /**
   * Get guest/anonymous token for checkout or chat
   * @param {string} email
   * @param {string} name
   * @param {string} urlPrefix - Not used for API calls (APIs don't have locale prefix)
   * @returns {Promise<{accessToken: string, user: Object}>}
   */
  async getGuestToken(email, name, urlPrefix = '/') {
    // API endpoints don't use locale prefix
    const response = await axios.post('/api/auth/guest', {
      email,
      name,
    });

    return response.data;
  }

  /**
   * Verify if a token is still valid
   * @param {string} token
   * @param {string} urlPrefix - Not used for API calls (APIs don't have locale prefix)
   * @returns {Promise<boolean>}
   */
  async verifyToken(token, urlPrefix = '/') {
    try {
      // API endpoints don't use locale prefix
      await axios.post(
        '/api/auth/verify',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Complete OAuth login (called from OAuth callback)
   * @param {string} provider - 'facebook', 'google', 'microsoft'
   * @param {string} code - Authorization code from OAuth provider
   * @param {string} state - State parameter for CSRF protection
   * @param {string} urlPrefix - Not used for API calls (APIs don't have locale prefix)
   * @returns {Promise<{accessToken: string, refreshToken: string, user: Object}>}
   */
  async oauthLogin(provider, code, state, urlPrefix = '/') {
    // API endpoints don't use locale prefix
    const response = await axios.post(`/api/auth/oauth/${provider}`, {
      code,
      state,
    });

    return response.data;
  }

  /**
   * Request password reset
   * @param {string} email
   * @param {string} urlPrefix - Not used for API calls (APIs don't have locale prefix)
   * @returns {Promise<void>}
   */
  async requestPasswordReset(email, urlPrefix = '/') {
    // API endpoints don't use locale prefix
    await axios.post('/api/auth/password-reset/request', {
      email,
    });
  }

  /**
   * Reset password with token
   * @param {string} token
   * @param {string} newPassword
   * @param {string} urlPrefix - Not used for API calls (APIs don't have locale prefix)
   * @returns {Promise<void>}
   */
  async resetPassword(token, newPassword, urlPrefix = '/') {
    // API endpoints don't use locale prefix
    await axios.post('/api/auth/password-reset/confirm', {
      token,
      newPassword,
    });
  }

  /**
   * Change password for authenticated user
   * @param {string} currentPassword
   * @param {string} newPassword
   * @param {string} urlPrefix - Not used for API calls (APIs don't have locale prefix)
   * @returns {Promise<void>}
   */
  async changePassword(currentPassword, newPassword, urlPrefix = '/') {
    // API endpoints don't use locale prefix
    await axios.post('/api/auth/password-change', {
      currentPassword,
      newPassword,
    });
  }

  /**
   * Impersonate a user
   * Creates an impersonation access token (4 hour expiration) for the target user
   * @param {string} username - Username of the user to impersonate
   * @param {string} urlPrefix - Not used for API calls (APIs don't have locale prefix)
   * @returns {Promise<{success: boolean, accessToken: string, user: Object, message: string}>}
   */
  async impersonate(username, urlPrefix = '/') {
    const response = await axios.post('/api/auth/impersonate', {
      username,
    }, {
      withCredentials: true, // Required to send httpOnly cookies
    });

    return response.data;
  }

  /**
   * Exit impersonation and return to original user
   * Generates a new access token for the original user
   * @param {string} urlPrefix - Not used for API calls (APIs don't have locale prefix)
   * @returns {Promise<{success: boolean, accessToken: string, user: Object, message: string}>}
   */
  async exitImpersonation(urlPrefix = '/') {
    const response = await axios.post('/api/auth/impersonation/exit', {}, {
      withCredentials: true, // Required to send httpOnly cookies
    });

    return response.data;
  }
}

// Export singleton instance
export default new AuthService();
